import fetcher, { formDataFetcher } from '../fetcher'

import config from '../../config'
import { JobOffer, JobOfferApplication, JobOfferEngagement, LatestAcademicTranscript, WorkShift } from './types'
import { IResponseBody } from '../types'
import { City } from '../companies/types'

export const getAll = async ({ limit }: { limit?: number }): Promise<Array<JobOffer> | undefined> => {
	try {
		const res: IResponseBody<Array<JobOffer>> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/job-offers/recommended?limit=${limit}`, {
			method: 'GET'
		})
		return res.data
	} catch (error: unknown) {
    console.error(`Error fetching job offers: ${error}`)
		throw error
	}
}

export const getBycompany = async ({ companyId, limit }: { companyId: number, limit?: number }): Promise<Array<JobOffer> | undefined> => {
	try {
		const res: IResponseBody<Array<JobOffer>> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/companies/${companyId}/job-offers?limit=${limit}`, {
			method: 'GET'
		})
		return res.data
	} catch (error: unknown) {
    console.error(`Error fetching job offers for company: ${error}`)
		throw error
	}
}

export const getById = async (id: number): Promise<JobOffer> => {
  try {
    const res = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/job-offers/${id}`, {
      method: 'GET'
    })
    return res.data
  } catch (error: unknown) {
    console.error(`Error fetching job offer: ${error}`)
    throw error
  }
}

export const getAllWorkShifts = async (): Promise<Array<WorkShift> | undefined> => {
	try {
		const res: IResponseBody<Array<WorkShift>> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/job-offers/work-shifts`, {
			method: 'GET'
		})
		return res.data
	} catch (error: unknown) {
    console.error(`Error fetching work shifts: ${error}`)
		throw error
	}
}

export const getAllSpainCities = async (): Promise<Array<City> | undefined> => {
	try {
		const res: IResponseBody<Array<City>> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/countries/1/cities`, {
			method: 'GET'
		})
		return res.data
	} catch (error: unknown) {
    console.error(`Error fetching work shifts: ${error}`)
		throw error
	}
}

export const applyJobOffer = async (_: string, { arg }: { arg: { body: Partial<JobOfferApplication> } }): Promise<JobOfferApplication> => {
	try {
		const { body } = arg
		const formData = new FormData()
		if(body.cv) { formData.append('cv', (body.cv as File)) }
		if(body.academicTranscript) { formData.append('academicTranscript', (body.academicTranscript as File)) }
		if(body.coverLetter) { formData.append('coverLetter', (body.coverLetter as File)) }
		formData.append('professionalContext', body.professionalContext ?? '')
		if(body.academicTranscriptId) {
			formData.append('academicTranscriptId', body.academicTranscriptId.toString())
		}
		if (body.canSignInternshipAgreement !== undefined && body.canSignInternshipAgreement !== null) {
			const value = body.canSignInternshipAgreement === true || body.canSignInternshipAgreement.toString() === 'true'
			formData.append('canSignInternshipAgreement', value ? 'true' : 'false')
		}
		if (body.languageLevels && Array.isArray(body.languageLevels) && body.languageLevels.length > 0) {
			formData.append('languageLevels', JSON.stringify(body.languageLevels))
		}

		const result = await formDataFetcher(
			`${config.VITE_ZRIVE_API_URL}/api/job-offers/${body.jobOfferId}/applications`,
			formData,
			'POST',
		)

		return result
	} catch (error: unknown) {
		console.error(`Error applying to job offer: ${error}`)
		throw error
	}
}

export const getAllJobOfferApplications = async (): Promise<JobOfferApplication[]> => {
	try {
		const res: IResponseBody<JobOfferApplication[]> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/job-offers/my-applications`, {
			method: 'GET'
		})
		return res.data as JobOfferApplication[]
	} catch (error: unknown) {
		console.error(`Error fetching latest application: ${error}`)
		throw error
	}
}

export const getJobOfferApplicationByOfferId = async (jobOfferId: number): Promise<JobOfferApplication | null> => {
	try {
		const res: IResponseBody<JobOfferApplication | null> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/job-offers/${jobOfferId}/my-application`, {
			method: 'GET'
		})
		return res.data as JobOfferApplication | null
	} catch (error: unknown) {
		console.error(`Error fetching job offer application by offer ID: ${error}`)
		throw error
	}
}

export const getJobOfferEngagements = async (): Promise<JobOfferEngagement[]> => {
	try {
		const res: IResponseBody<JobOfferEngagement[]> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/job-offers/engaged`, {
			method: 'GET'
		})
		return res.data as JobOfferEngagement[]
	} catch (error: unknown) {
		console.error(`Error fetching job offer engagements: ${error}`)
		throw error
	}
}

export const getLatestAcademicTranscript = async (): Promise<LatestAcademicTranscript | undefined> => {
	try {
		const res: IResponseBody<LatestAcademicTranscript> = await fetcher(`${config.VITE_ZRIVE_API_URL}/api/users/academic-transcript`, {
			method: 'GET'
		})
		return res.data
	} catch (error: unknown) {
		console.error(`Error fetching latest academic transcript: ${error}`)
		throw error
	}
}
